/**
 * Error Boundary Component
 *
 * Catches React errors in the component tree and displays a fallback UI.
 * Prevents the entire app from crashing when an error occurs.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Optional: Send to error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback && this.state.error && this.state.errorInfo) {
        return this.props.fallback(this.state.error, this.state.errorInfo);
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

// =============================================================================
// Default Error Fallback
// =============================================================================

interface DefaultErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({
  error,
  onReset,
}) => {
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>⚠️</div>
        <h1 style={styles.title}>Something went wrong</h1>
        <p style={styles.description}>
          The application encountered an unexpected error. Don't worry, your
          data is safe.
        </p>

        {error && (
          <details style={styles.details}>
            <summary style={styles.summary}>Error details</summary>
            <pre style={styles.errorMessage}>{error.toString()}</pre>
            {error.stack && <pre style={styles.errorStack}>{error.stack}</pre>}
          </details>
        )}

        <div style={styles.actions}>
          <button onClick={onReset} style={styles.primaryButton}>
            Try Again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            style={styles.secondaryButton}
          >
            Go to Home
          </button>
        </div>

        <p style={styles.hint}>
          If the problem persists, try clearing your browser cache or reloading
          the page.
        </p>
      </div>
    </div>
  );
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eee9fa",
    padding: "24px",
  },
  card: {
    maxWidth: "600px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "48px",
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
    textAlign: "center",
  },
  icon: {
    fontSize: "64px",
    marginBottom: "24px",
  },
  title: {
    margin: "0 0 16px 0",
    fontSize: "28px",
    fontWeight: "700",
    fontFamily: '"Playfair Display", serif',
    color: "#2b1a45",
  },
  description: {
    margin: "0 0 32px 0",
    fontSize: "16px",
    color: "#6b7280",
    lineHeight: "1.6",
  },
  details: {
    textAlign: "left",
    marginBottom: "32px",
    backgroundColor: "#eee9fa",
    border: "1px solid #d8cff3",
    borderRadius: "8px",
    padding: "16px",
  },
  summary: {
    cursor: "pointer",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "12px",
  },
  errorMessage: {
    margin: "12px 0",
    fontSize: "14px",
    color: "#dc2626",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "monospace",
  },
  errorStack: {
    margin: "12px 0",
    fontSize: "12px",
    color: "#6b7280",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    fontFamily: "monospace",
    maxHeight: "200px",
    overflow: "auto",
  },
  actions: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    marginBottom: "24px",
  },
  primaryButton: {
    padding: "12px 32px",
    backgroundColor: "#6a49a8",
    color: "#ffffff",
    border: "none",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  secondaryButton: {
    padding: "10px 30px",
    backgroundColor: "#ffffff",
    color: "#6a49a8",
    border: "2px solid #6a49a8",
    borderRadius: "999px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  hint: {
    margin: 0,
    fontSize: "14px",
    color: "#9ca3af",
  },
};

export default ErrorBoundary;
